import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import {
  useAccount,
  useBalance,
  useChainId,
  useReadContract,
  useWaitForTransactionReceipt,
  useWriteContract,
} from 'wagmi';
import { formatEther, formatUnits, isAddress, type Address } from 'viem';
import { useQueryClient } from '@tanstack/react-query';
import { erc20Abi } from '../abi/erc20';
import { splitterAbi, splitterReleaseErc20Abi, splitterReleasableErc20Abi } from '../abi/splitter';
import { SplitterTxHistory } from './SplitterTxHistory';
import { storageKeys } from '../config/brand';

function tokenStorageKey(chainId: number, splitter: Address) {
  return storageKeys.erc20Splitter(chainId, splitter.toLowerCase());
}

function legacyTokenStorageKey(chainId: number, splitter: Address) {
  return `evm-splitter:erc20:${chainId}:${splitter.toLowerCase()}`;
}

function loadTokens(chainId: number, splitter: Address): Address[] {
  try {
    const key = tokenStorageKey(chainId, splitter);
    let raw = localStorage.getItem(key);
    if (!raw) {
      raw = localStorage.getItem(legacyTokenStorageKey(chainId, splitter));
      if (raw) {
        localStorage.setItem(key, raw);
      }
    }
    if (!raw) return [];
    const arr = JSON.parse(raw) as string[];
    return arr.filter((a) => isAddress(a)) as Address[];
  } catch {
    return [];
  }
}

function saveTokens(chainId: number, splitter: Address, tokens: Address[]) {
  localStorage.setItem(tokenStorageKey(chainId, splitter), JSON.stringify(tokens));
}

const MAX_SPLITTER_LABEL_LEN = 80;

function labelStorageKey(chainId: number, splitter: Address) {
  return storageKeys.splitterLabel(chainId, splitter.toLowerCase());
}

function loadSplitterLabel(chainId: number, splitter: Address): string {
  try {
    const v = localStorage.getItem(labelStorageKey(chainId, splitter));
    return v?.trim().slice(0, MAX_SPLITTER_LABEL_LEN) ?? '';
  } catch {
    return '';
  }
}

function saveSplitterLabel(chainId: number, splitter: Address, label: string) {
  const t = label.trim().slice(0, MAX_SPLITTER_LABEL_LEN);
  try {
    if (!t) {
      localStorage.removeItem(labelStorageKey(chainId, splitter));
    } else {
      localStorage.setItem(labelStorageKey(chainId, splitter), t);
    }
  } catch {
    /* ignore quota / private mode */
  }
}

type Props = {
  splitterAddress: Address;
  explorerBaseUrl: string | undefined;
};

export function SplitterPanel({ splitterAddress, explorerBaseUrl }: Props) {
  const { address: user } = useAccount();
  const chainId = useChainId();
  const queryClient = useQueryClient();

  const [tokenInput, setTokenInput] = useState('');
  const [tracked, setTracked] = useState<Address[]>([]);
  const [labelDraft, setLabelDraft] = useState('');
  const [savedLabel, setSavedLabel] = useState('');
  const [editingLabel, setEditingLabel] = useState(false);

  useLayoutEffect(() => {
    setTracked(loadTokens(chainId, splitterAddress));
    const fromStore = loadSplitterLabel(chainId, splitterAddress);
    setSavedLabel(fromStore);
    setLabelDraft(fromStore);
    setEditingLabel(false);
  }, [chainId, splitterAddress]);

  const commitLabel = () => {
    saveSplitterLabel(chainId, splitterAddress, labelDraft);
    setSavedLabel(labelDraft.trim().slice(0, MAX_SPLITTER_LABEL_LEN));
    setEditingLabel(false);
  };

  const cancelLabelEdit = () => {
    setLabelDraft(savedLabel);
    setEditingLabel(false);
  };

  const startLabelEdit = () => {
    setLabelDraft(savedLabel);
    setEditingLabel(true);
  };

  const { data: owner } = useReadContract({
    address: splitterAddress,
    abi: splitterAbi,
    functionName: 'owner',
    query: { enabled: Boolean(user) },
  });

  const { data: totalShares } = useReadContract({
    address: splitterAddress,
    abi: splitterAbi,
    functionName: 'totalShares',
    query: { enabled: !!user },
  });

  const { data: userShares } = useReadContract({
    address: splitterAddress,
    abi: splitterAbi,
    functionName: 'shares',
    args: user ? [user] : undefined,
    query: { enabled: !!user },
  });

  const { data: payeeCount } = useReadContract({
    address: splitterAddress,
    abi: splitterAbi,
    functionName: 'payeeCount',
    query: { enabled: !!user },
  });

  const { data: protocolFeeBps } = useReadContract({
    address: splitterAddress,
    abi: splitterAbi,
    functionName: 'protocolFeeBps',
    query: { enabled: !!user },
  });

  const { data: nativeReleasable } = useReadContract({
    address: splitterAddress,
    abi: splitterAbi,
    functionName: 'releasable',
    args: user ? [user] : undefined,
    query: { enabled: !!user },
  });

  const { data: nativeBal } = useBalance({
    address: splitterAddress,
    chainId,
    query: { enabled: !!user },
  });

  const { writeContract, data: hash, error: writeError, isPending } = useWriteContract();
  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({ hash });

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries();
  }, [queryClient]);

  useEffect(() => {
    if (isSuccess) invalidate();
  }, [isSuccess, invalidate]);

  const sharePct = useMemo(() => {
    if (userShares === undefined || totalShares === undefined || totalShares === BigInt(0)) return null;
    return Number((userShares * BigInt(10000)) / totalShares) / 100;
  }, [userShares, totalShares]);

  const payeeIndices = useMemo(() => {
    const n = payeeCount !== undefined ? Number(payeeCount) : 0;
    return Array.from({ length: n }, (_, i) => i);
  }, [payeeCount]);

  const explorerHref = explorerBaseUrl
    ? `${explorerBaseUrl.replace(/\/$/, '')}/address/${splitterAddress}`
    : undefined;

  const addToken = () => {
    const t = tokenInput.trim();
    if (!isAddress(t)) return;
    const a = t as Address;
    if (tracked.some((x) => x.toLowerCase() === a.toLowerCase())) return;
    const next = [...tracked, a];
    setTracked(next);
    saveTokens(chainId, splitterAddress, next);
    setTokenInput('');
  };

  const removeToken = (t: Address) => {
    const next = tracked.filter((x) => x.toLowerCase() !== t.toLowerCase());
    setTracked(next);
    saveTokens(chainId, splitterAddress, next);
  };

  const claimNative = () => {
    if (!user) return;
    writeContract({
      address: splitterAddress,
      abi: splitterAbi,
      functionName: 'release',
      args: [user],
    });
  };

  const claimErc20 = (token: Address) => {
    if (!user) return;
    writeContract({
      address: splitterAddress,
      abi: splitterReleaseErc20Abi,
      functionName: 'release',
      args: [token, user],
    });
  };

  return (
    <div className="card bg-neutral text-neutral-content shadow-sm border border-neutral-700/80">
      <div className="card-body gap-3">
        <div className="space-y-2">
          {editingLabel && (
            <div className="form-control w-full max-w-lg">
              <div className="flex flex-wrap gap-2 items-center">
                <input
                  type="text"
                  maxLength={MAX_SPLITTER_LABEL_LEN}
                  placeholder="e.g. Team revenue split"
                  className="input input-bordered input-sm flex-1 min-w-[12rem]"
                  value={labelDraft}
                  autoFocus
                  onChange={(e) => setLabelDraft(e.target.value.slice(0, MAX_SPLITTER_LABEL_LEN))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitLabel();
                    if (e.key === 'Escape') cancelLabelEdit();
                  }}
                />
                <button type="button" className="btn btn-primary btn-sm shrink-0" onClick={commitLabel}>
                  Save
                </button>
                <button type="button" className="btn btn-ghost btn-sm shrink-0" onClick={cancelLabelEdit}>
                  Cancel
                </button>
              </div>
            </div>
          )}
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              {savedLabel ? (
                <>
                  <div className="flex items-center gap-1 flex-wrap">
                    <h3 className="font-semibold text-base leading-tight">{savedLabel}</h3>
                    {!editingLabel && (
                      <button
                        type="button"
                        className="btn btn-ghost btn-xs btn-square shrink-0 opacity-70 hover:opacity-100"
                        onClick={startLabelEdit}
                        aria-label="Edit split name"
                      >
                        <PencilIcon />
                      </button>
                    )}
                  </div>
                  <p className="font-mono text-xs opacity-80 break-all mt-0.5">
                    {explorerHref ? (
                      <a href={explorerHref} target="_blank" rel="noreferrer" className="link link-primary">
                        {splitterAddress}
                      </a>
                    ) : (
                      splitterAddress
                    )}
                  </p>
                </>
              ) : (
                <div className="flex items-start gap-1 flex-wrap">
                  <h3 className="font-mono text-sm font-semibold break-all min-w-0">
                    {explorerHref ? (
                      <a href={explorerHref} target="_blank" rel="noreferrer" className="link link-primary">
                        {splitterAddress}
                      </a>
                    ) : (
                      splitterAddress
                    )}
                  </h3>
                  {!editingLabel && (
                    <button
                      type="button"
                      className="btn btn-ghost btn-xs btn-square shrink-0 opacity-70 hover:opacity-100"
                      onClick={() => {
                        setLabelDraft('');
                        setEditingLabel(true);
                      }}
                      aria-label="Add split name"
                    >
                      <PencilIcon />
                    </button>
                  )}
                </div>
              )}
            </div>
            {owner != null && (
              <span className="text-xs opacity-80 shrink-0">
                Owner: <span className="font-mono">{String(owner)}</span>
              </span>
            )}
          </div>
        </div>

        {protocolFeeBps !== undefined && (
          <p className="text-xs opacity-75">
            Protocol fee: {(Number(protocolFeeBps) / 100).toFixed(2)}% of each claim ({protocolFeeBps.toString()} bps)
          </p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
          <div>
            <span className="opacity-70">Your shares</span>{' '}
            <span className="font-mono">
              {userShares !== undefined && totalShares !== undefined
                ? `${userShares.toString()} / ${totalShares.toString()}`
                : '—'}
              {sharePct != null && ` (${sharePct.toFixed(2)}%)`}
            </span>
          </div>
          <div>
            <span className="opacity-70">Contract native balance</span>{' '}
            <span className="font-mono">
              {nativeBal !== undefined ? `${formatEther(nativeBal.value)} ${nativeBal.symbol}` : '—'}
            </span>
          </div>
          <div>
            <span className="opacity-70">Native releasable to you</span>{' '}
            <span className="font-mono text-primary">
              {nativeReleasable !== undefined ? formatEther(nativeReleasable) : '—'}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={
              !user ||
              nativeReleasable === undefined ||
              nativeReleasable === BigInt(0) ||
              isPending ||
              isConfirming
            }
            onClick={claimNative}
          >
            {isPending || isConfirming ? 'Confirm…' : 'Claim native'}
          </button>
        </div>

        <div className="divider my-1">ERC-20 tokens</div>
        <p className="text-xs opacity-80 leading-relaxed">
          Track any ERC-20 sent to this contract. Claim calls the standard OpenZeppelin{' '}
          <code className="text-[10px] opacity-90">release(token, account)</code> — same pull-payment model as native
          claims. Add each token contract address below, then claim your share when releasable balance is non-zero.{' '}
          <span className="opacity-90">Tracked tokens are saved in this browser for this split.</span>
        </p>
        <div className="flex flex-wrap gap-2">
          <input
            type="text"
            placeholder="0x… token"
            className="input input-bordered input-sm flex-1 min-w-[200px] font-mono"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
          />
          <button type="button" className="btn btn-secondary btn-sm" onClick={addToken}>
            Track token
          </button>
        </div>

        <div className="space-y-2">
          {tracked.map((token) => (
            <TokenRow
              key={token}
              splitter={splitterAddress}
              token={token}
              user={user}
              onRemove={() => removeToken(token)}
              onClaim={() => claimErc20(token)}
              disabled={isPending || isConfirming}
            />
          ))}
        </div>

        {payeeIndices.length > 0 && (
          <details className="collapse collapse-arrow bg-base-100/50 rounded-box">
            <summary className="collapse-title text-sm font-medium">Payees</summary>
            <div className="collapse-content text-sm space-y-1">
              {payeeIndices.map((i) => (
                <PayeeRow key={i} splitter={splitterAddress} index={i} />
              ))}
            </div>
          </details>
        )}

        <SplitterTxHistory splitterAddress={splitterAddress} explorerBaseUrl={explorerBaseUrl} />

        {(writeError || (isSuccess && hash)) && (
          <p className="text-xs break-all opacity-80">
            {writeError ? String(writeError.message) : `Tx: ${hash}`}
          </p>
        )}
      </div>
    </div>
  );
}

function PencilIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      className="w-4 h-4"
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125"
      />
    </svg>
  );
}

function PayeeRow({ splitter, index }: { splitter: Address; index: number }) {
  const { data: addr } = useReadContract({
    address: splitter,
    abi: splitterAbi,
    functionName: 'payee',
    args: [BigInt(index)],
  });
  const { data: sh } = useReadContract({
    address: splitter,
    abi: splitterAbi,
    functionName: 'shares',
    args: addr ? [addr] : undefined,
    query: { enabled: !!addr },
  });
  return (
    <div className="flex flex-wrap gap-2 font-mono">
      <span>{addr ? String(addr) : '…'}</span>
      {sh !== undefined && <span className="opacity-70">shares {sh.toString()}</span>}
    </div>
  );
}

function TokenRow({
  splitter,
  token,
  user,
  onRemove,
  onClaim,
  disabled,
}: {
  splitter: Address;
  token: Address;
  user: Address | undefined;
  onRemove: () => void;
  onClaim: () => void;
  disabled: boolean;
}) {
  const { data: symbol } = useReadContract({
    address: token,
    abi: erc20Abi,
    functionName: 'symbol',
  });
  const { data: decimals } = useReadContract({
    address: token,
    abi: erc20Abi,
    functionName: 'decimals',
  });
  const { data: bal } = useReadContract({
    address: token,
    abi: erc20Abi,
    functionName: 'balanceOf',
    args: [splitter],
  });

  const { data: rel } = useReadContract({
    address: splitter,
    abi: splitterReleasableErc20Abi,
    functionName: 'releasable',
    args: user ? [token, user] : undefined,
    query: { enabled: Boolean(user) },
  });

  const formatted =
    rel !== undefined && decimals !== undefined
      ? formatUnits(rel, decimals)
      : rel !== undefined
        ? rel.toString()
        : '—';

  const balFormatted =
    bal !== undefined && decimals !== undefined
      ? formatUnits(bal, decimals)
      : bal !== undefined
        ? bal.toString()
        : '—';

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-base-100/40 p-2">
      <div className="min-w-0">
        <div className="font-mono text-xs truncate">
          {symbol ?? '…'} · {token}
        </div>
        <div className="text-xs opacity-80">
          In contract: {balFormatted} · Releasable to you: {formatted}
        </div>
      </div>
      <div className="flex gap-1 shrink-0">
        <button type="button" className="btn btn-ghost btn-xs" onClick={onRemove}>
          Untrack
        </button>
        <button
          type="button"
          className="btn btn-primary btn-xs"
          disabled={!user || rel === undefined || rel === BigInt(0) || disabled}
          onClick={onClaim}
        >
          Claim
        </button>
      </div>
    </div>
  );
}
